import React, { useEffect, useRef, useState, useCallback } from "react";
import LoadingSpinnerImage from "../TaskManagerApp/LoadingSpinnerImage.js";

const ResponsiveIframe = ({ src, onEmptyStateChange, maxRetries = 3 }) => {
    const iframeRef = useRef(null);
    const observerRef = useRef(null);
    const [isLoading, setIsLoading] = useState(true);
    const [retryCount, setRetryCount] = useState(0);
    const [hasFailed, setHasFailed] = useState(false);

    const [iframeHeight, setIframeHeight] = useState("700px");

    const currentSrc = retryCount > 0
        ? `${src}${src.includes("?") ? "&" : "?"}retry=${retryCount}`
        : src;

    const triggerRetry = useCallback((isManual = false) => {
        if (retryCount < maxRetries || isManual) {
            setRetryCount(prev => prev + 1);
            setIsLoading(true);
            setHasFailed(false);
            console.warn(`Iframe failed to load. Retrying... Attempt ${retryCount + 1}`);
        } else {
            setHasFailed(true);
            setIsLoading(false);
            console.error("Iframe reached maximum retry attempts.");
        }
    }, [retryCount, maxRetries]);

    const resizeIframe = () => {
        const iframe = iframeRef.current;
        if (iframe) {
            try {
                const iframeDocument = iframe.contentDocument || iframe.contentWindow.document;

                if (iframeDocument && iframeDocument.body) {
                    const newHeight = Math.max(
                        iframeDocument.body.scrollHeight,
                        iframeDocument.documentElement.scrollHeight,
                        iframeDocument.body.offsetHeight,
                        iframeDocument.documentElement.offsetHeight
                    );

                    const isEffectivelyEmpty = newHeight <= 30 || iframeDocument.body.innerHTML.trim() === "";

                    if (onEmptyStateChange) {
                        onEmptyStateChange(isEffectivelyEmpty);
                    }

                    if (!isEffectivelyEmpty && newHeight > 30) {
                        // FIX: Update React State instead of the DOM element directly
                        setIframeHeight(`${newHeight}px`);
                    }
                }
            } catch (error) {
                console.warn("Cannot access iframe content due to cross-origin restrictions.");
                // FIX: Update React state for cross-origin fallback
                setIframeHeight("100vh");
                if (onEmptyStateChange) onEmptyStateChange(false);
            }
        }
    };

    const handleIframeLoad = () => {
        const iframe = iframeRef.current;

        if (iframe) {
            try {
                const iframeDocument = iframe.contentDocument || iframe.contentWindow.document;
                if (iframeDocument && iframeDocument.body) {
                    if (iframeDocument.body.innerHTML.trim() === "" && retryCount < maxRetries) {
                        triggerRetry();
                        return;
                    }
                }
            } catch (error) {
                // Ignore cross-origin errors
            }
        }

        setIsLoading(false);
        setHasFailed(false);

        // Timeout ensures the DOM is fully painted before calculating height
        setTimeout(() => resizeIframe(), 50);

        if (iframe) {
            try {
                const iframeDocument = iframe.contentDocument || iframe.contentWindow.document;

                if (iframeDocument && iframeDocument.body) {
                    if (observerRef.current) {
                        observerRef.current.disconnect();
                    }

                    observerRef.current = new MutationObserver(() => {
                        resizeIframe();
                    });

                    observerRef.current.observe(iframeDocument.body, {
                        childList: true,
                        subtree: true,
                        attributes: true
                    });
                }
            } catch (error) {
                // Expected block for cross-origin URLs
            }
        }
    };

    useEffect(() => {
        setIsLoading(true);
        setRetryCount(0);
        setHasFailed(false);

        // FIX: Reset height via state
        setIframeHeight("700px");
    }, [src]);

    useEffect(() => {
        const handleWindowResize = () => {
            if (!isLoading && !hasFailed) {
                resizeIframe();
            }
        };

        window.addEventListener("resize", handleWindowResize);

        return () => {
            window.removeEventListener("resize", handleWindowResize);
            if (observerRef.current) {
                observerRef.current.disconnect();
            }
        };
    }, [isLoading, hasFailed]);

    useEffect(() => {
        let timer;
        if (isLoading && !hasFailed) {
            timer = setTimeout(() => {
                triggerRetry();
            }, 15000);
        }
        return () => clearTimeout(timer);
    }, [isLoading, hasFailed, triggerRetry]);

    return (
        <div style={{ width: "100%" }}>

            {isLoading && !hasFailed && (
                <div style={{ width: "100%", textAlign: "center", padding: "40px" }}>
                    <LoadingSpinnerImage />
                    {retryCount > 0 && (
                        <div style={{ marginTop: "10px", fontSize: "0.85em", color: "#6c757d" }}>
                            Retrying connection (Attempt {retryCount}/{maxRetries})...
                        </div>
                    )}
                </div>
            )}

            {hasFailed && (
                <div style={{ width: "100%", textAlign: "center", padding: "40px", backgroundColor: "#f8f9fa", borderRadius: "8px" }}>
                    <i className="icon-warning" style={{ fontSize: "2rem", color: "#6c757d", marginBottom: "10px", display: "block" }}></i>
                    <h5 style={{ color: "#495057" }}>Content Failed to Load</h5>
                    <p style={{ color: "#6c757d", fontSize: "0.9rem" }}>We're having trouble reaching the document server.</p>
                    <button
                        className="btn btn-outline-secondary mt-2"
                        onClick={() => triggerRetry(true)}
                    >
                        <i className="icon-refresh mr-2"></i> Try Again
                    </button>
                </div>
            )}

            {!hasFailed && (
                <iframe
                    key={retryCount}
                    ref={iframeRef}
                    onLoad={handleIframeLoad}
                    onError={() => triggerRetry()}
                    src={currentSrc}
                    frameBorder="0"
                    scrolling="no"
                    style={{
                        width: "100%",
                        // FIX: Bind the inline style to the React state variable
                        height: iframeHeight,
                        display: "block",
                        visibility: isLoading ? "hidden" : "visible",
                        transition: "height 0.3s ease"
                    }}
                    title="Responsive Content"
                />
            )}
        </div>
    );
};

export default ResponsiveIframe;